import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, Instagram } from 'lucide-react';
import { companyInfo, services, socialMedia } from '../mockData';

const iconMap = { Linkedin, Instagram };

const quickLinks = [
  { label: 'Home', path: '/' },
  { label: 'About Us', path: '/about' },
  { label: 'Our Services', path: '/services' },
  { label: 'Projects', path: '/projects' },
  { label: 'Our Team', path: '/team' },
  { label: 'Contact Us', path: '/contact' }
];

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-charcoal text-white py-12 border-t border-gray-800" data-testid="site-footer">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Company Info */}
          <div>
            <div className="mb-4">
              <img
                src="/logo.png"
                alt="Alakhe Conglomerate"
                className="h-16 w-auto object-contain mb-4 brightness-0 invert"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div style={{ display: 'none' }}>
                <h3 className="text-brand-red font-bold text-xl mb-1">ALAKHE</h3>
                <p className="text-white text-sm font-semibold tracking-wider mb-3">CONGLOMERATE</p>
              </div>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-4">
              Driving value and adding solutions across construction, infrastructure, and industrial sectors.
            </p>
            <p className="text-gray-500 text-xs mb-4">Reg: {companyInfo.registration}</p>
            <div className="flex space-x-3">
              {socialMedia.map((social) => {
                const Icon = iconMap[social.icon];
                return (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.name}
                    data-testid={`footer-social-${social.name.toLowerCase()}`}
                    className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center hover:bg-brand-red transition-colors"
                  >
                    <Icon className="w-4 h-4 text-white" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-bold mb-4 text-sm uppercase tracking-wider">Quick Links</h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    data-testid={`footer-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                    className="text-gray-400 hover:text-brand-red text-sm transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-bold mb-4 text-sm uppercase tracking-wider">Our Services</h4>
            <ul className="space-y-2">
              {services.map((service) => (
                <li key={service.id}>
                  <Link
                    to="/services"
                    className="text-gray-400 hover:text-brand-red text-sm transition-colors"
                  >
                    {service.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-bold mb-4 text-sm uppercase tracking-wider">Contact Us</h4>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-brand-red flex-shrink-0 mt-0.5" />
                <a
                  href={`mailto:${companyInfo.email.admin}`}
                  className="text-gray-400 hover:text-brand-red text-sm transition-colors block"
                >
                  {companyInfo.email.admin}
                </a>
              </div>
              <div className="flex items-start space-x-3">
                <Phone className="w-5 h-5 text-brand-red flex-shrink-0 mt-0.5" />
                <a
                  href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
                  className="text-gray-400 hover:text-brand-red text-sm transition-colors"
                >
                  {companyInfo.phone}
                </a>
              </div>
              <div className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-brand-red flex-shrink-0 mt-0.5" />
                <p className="text-gray-400 text-sm">{companyInfo.address}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-8">
  <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
    <div className="flex flex-col sm:flex-row items-center gap-2 text-sm">
      <p className="text-gray-500">
        Developed by{' '}
        <a
          href="https://www.linkedin.com/in/paul-manchu-610583198/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-gray-400 hover:text-brand-red transition-colors"
        >
          Paul Manchu
        </a>
      </p>

      <span className="hidden sm:inline text-gray-700">|</span>

      <p className="text-gray-500">
        © {currentYear} Alakhe Conglomerate (Pty) Ltd. All rights reserved.
      </p>
    </div>

    <div className="flex space-x-6">
      <Link
        to="/privacy-policy"
        className="text-gray-500 hover:text-brand-red text-sm transition-colors"
      >
        Privacy Policy
      </Link>

      <Link
        to="/terms-of-use"
        className="text-gray-500 hover:text-brand-red text-sm transition-colors"
      >
        Terms of Use
      </Link>
    </div>
  </div>
</div>
</div>
</footer>
  );
};

export default Footer;
